import React, { useEffect, useState } from 'react';

import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableTransactions,
  RequestTransactionInterface,
} from '../../interface/transaction-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import {
  fetchAllTransactions,
  fetchListFilters,
  setSortData,
  setTransactionFilterData,
  updateTransactionFilters,
} from '../../redux/slice/TransactionManagementSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ExportButton from '../../shared-components/export-button/ExportButton';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import EndConsumerSidePanel from '../../shared-components/table-side-panel/EndConsumerSidePanel';
import StationSidePanel from '../../shared-components/table-side-panel/StationSidePanel';
import TransactionSidePanel from '../../shared-components/table-side-panel/TransactionSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import { CapitalizeFirstLetter } from '../../shared-functions/StringFunctions';
import transactionApi from '../../utils/api/transaction';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

/**
 * Transaction management module
 */
function TransactionManagement() {
  const dispatch = useAppDispatch();
  const {
    transactions,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
  } = useAppSelector((state) => state.transaction);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const { sortBy, sortField } = useAppSelector((state) => state.transaction);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestTransactionInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllTransactions({
          ...data,
        }),
      );
    }
  };

  const column = [
    {
      label: 'S. No',
      accessorKey: '' as keyof AvailableTransactions,
      /**
       * S.No view
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 40,
    },
    {
      label: 'Date',
      accessorKey: 'date' as keyof AvailableTransactions,
      sortAllow: true,
    },
    {
      label: 'Time',
      accessorKey: 'time' as keyof AvailableTransactions,
      sortAllow: true,
    },
    {
      label: 'Transaction Id',
      // accessorKey: 'transactionId' as keyof AvailableTransactions,
      accessorKey: '_id' as keyof AvailableTransactions,
      sortAllow: true,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: <TransactionSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Station Name',
      accessorKey: 'chargingStation' as keyof AvailableTransactions,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Device Id',
      accessorKey: 'chargingPointSn' as keyof AvailableTransactions,
      sortAllow: true,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'User Name',
      accessorKey: 'endConsumer' as keyof AvailableTransactions,
      sortAllow: true,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Power Consumed',
      accessorKey: 'powerConsumed' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Amount view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {Number.isNaN(parseFloat(transaction.powerConsumed))
              ? '0.00'
              : parseFloat(transaction.powerConsumed).toFixed(2)}
            Kwh
          </p>
        </div>
      ),
    },
    {
      label: 'Total Amount',
      accessorKey: 'amount' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Amount view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {Number.isNaN(parseFloat(transaction.walletHistory))
              ? '0.00'
              : parseFloat(transaction.walletHistory).toFixed(2)}
            /-
          </p>
        </div>
      ),
    },
    {
      label: 'Revenue Share',
      accessorKey: 'foRevenue' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Amount view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {Number.isNaN(parseFloat(transaction.foRevenue))
              ? '0.00'
              : parseFloat(transaction.foRevenue).toFixed(2)}
            /-
          </p>
        </div>
      ),
    },
    {
      label: 'Payment Status',
      accessorKey: 'paymentStatus' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Payment status view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="status-in-transaction-table">
          <p
            className={`table-row-each-data ${transaction.paymentStatus === 'paid' ? 'success-text' : 'error-text'}`}>
            {CapitalizeFirstLetter(transaction.paymentStatus)}
          </p>
        </div>
      ),
    },
    {
      label: 'Direct Start Transaction',
      accessorKey: 'directStartTransactionEvent' as keyof AvailableTransactions,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: AvailableTransactions) => (
        <div>{currentRow?.directStartTransactionEvent ? 'Yes' : 'No'}</div>
      ),
    },
    {
      label: 'Free Transaction',
      accessorKey: 'isFreeTransaction' as keyof AvailableTransactions,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: AvailableTransactions) => (
        <div>{currentRow?.isFreeTransaction ? 'Yes' : 'No'}</div>
      ),
    },
    {
      label: 'Rfid Card',
      accessorKey: 'rfidNo' as keyof AvailableTransactions,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: AvailableTransactions) => <div>{currentRow?.rfidNo}</div>,
    },
  ];
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: limitCount,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (Object.keys(sortData).length > 0) {
      dispatch(
        setTransactionFilterData({
          ...sortData,
        }),
      );
      dispatch(setSortData(sortData));
      fetchData({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    transactionApi
      .fetchTransactionExport({ ...filters })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          'Date',
          'Time',
          'Station Name',
          'Device Id',
          'User Name',
          'Amount',
          'Payment Status',
        ];
        const rows = response.data.results.map((row: AvailableTransactions) => [
          row.createdAt ? ConvertUTCtoDate(row.createdAt) : '',
          row.createdAt ? FormatUtcString(ConvertUTCtoHHMM(row.createdAt)) : '',
          row.chargingStation || '',
          row.chargingPointSn || '',
          row.endConsumer || '0',
          row.walletHistory ? `${parseInt(row?.walletHistory, 10).toFixed(2)}/-` : '0/-',
          row.paymentStatus || '',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'transaction-list.csv');
          } else {
            exportPDF(headers, rows, 'transaction-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="transaction-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="transaction-management-header">
        <PageTitle title="Transaction Management" />
      </div>
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={transactions}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          footerLeft={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <ExportButton
                  label="Export Data"
                  buttonList={[
                    {
                      key: 'pdf',
                      allow: true,
                      /**
                       * Export pdf function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('pdf'),
                    },
                    {
                      key: 'csv',
                      allow: true,
                      /**
                       * Export csv function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('csv'),
                    },
                  ]}
                  disabled={transactions.length === 0}
                  loader={exportLoader}
                />
              </div>
            </div>
          }
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateTransactionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
        />
      </div>
    </div>
  );
}

export default TransactionManagement;
